// Third party libraries
import _ from 'lodash';
import React, { MouseEvent } from 'react';
import { connect } from 'react-redux';
import { autofill, Field, InjectedFormProps, initialize, reduxForm } from 'redux-form';
import { Container, Form, Popup, PopupContent, PopupHeader, Segment } from 'semantic-ui-react';
import ISO6391 from 'iso-639-1';

// Redux
import { updateProfile } from '../../../../redux-store/auth';
import { closeLoader, openLoader } from '../../../../redux-store/loader';
import { openToast } from '../../../../redux-store/toast';

// Models
import FilesModel from '../../../../models/files';
import SecurityModel, { ProfileInterface } from '../../../../models/security';
import PaymentModel from '../../../../models/payment';

// Components
import BottomNav from '../../../components/bottomNav/BottomNav';
import CustomInput from '../../../components/form/input';
import CustomSelect, { FormSelectOptionsInterface } from '../../../components/form/select';
import CustomTextArea from '../../../components/form/textarea';
import MultimediaManage, { MultimediaTypes } from '../../../components/multimediaManage';
import PanelNav from '../../../components/nav/PanelNav';
import SetupNav from '../components/SetupNav';
import CustomCheckBox from '../../../components/form/checkbox';
import CustomSchedules from '../../../components/form/schedules';
import CustomPracticeModalities from '../../../components/form/modalities';
import VerifyStripeAccount from './components/VerifyStripeAccount';

// Styled components
import { Wrapper } from '../../../styled/wrappers';
import OButton from '../../../styled/button';

// Constants
import Countries from './constants/countries';
import Specialties from './constants/specialties'

// Locales
import I18n from '../../../../i18n';

// Styles
import './index.scss';

// Interfaces
interface FormProfileInterface extends ProfileInterface {
    instagram?: string;
    facebook?: string;
    web?: string;
    genderSelected?: string;
};

interface MapDispatchProps {
    openLoader: any;
    closeLoader: any;
    reduxFormState: any;
    updateProfile: any;
};

interface ProfileProps extends MapDispatchProps {
    handleSubmit?: any;
    invalid?: boolean;
    navigate?: any;
    dispatch?: any;
};

interface ProfileState {
    countrySelectOptions: FormSelectOptionsInterface[];
    genderSelectOptions: FormSelectOptionsInterface[];
    languageSelectOptions: FormSelectOptionsInterface[];
    specialtiesSelectOptions: FormSelectOptionsInterface[];
    profile: ProfileInterface;
    isPublishableProfile: boolean;
    publishMissingFields: string[];
    refundPercent: number;
    isImageDeleted: boolean;
    isVideoDeleted: boolean;
};

class ProfileData extends React.Component<ProfileProps & InjectedFormProps<{}, ProfileProps>, ProfileState> {

    constructor(props: ProfileProps & InjectedFormProps<{}, ProfileProps>) {

        super(props);

        this.state = {
            countrySelectOptions: [],
            genderSelectOptions: [{
                key: '1',
                text: I18n.t('setup.genders.1'),
                value: '1'
            }, {
                key: '0',
                text: I18n.t('setup.genders.0'),
                value: '0'
            }],
            languageSelectOptions: [],
            specialtiesSelectOptions: [],
            profile: { email: '', _id: '' },
            isPublishableProfile: false,
            publishMissingFields: [],
            refundPercent: 10,
            isImageDeleted: false,
            isVideoDeleted: false
        };

        _.each(Countries, (country, key) => this.state.countrySelectOptions.push({ key, text: country, value: key }));
        _.each(Specialties, (specialty, key) => this.state.specialtiesSelectOptions.push({ key, text: specialty, value: key }));
        _.each(ISO6391.getLanguages(ISO6391.getAllCodes()), language => this.state.languageSelectOptions.push({ key: language.code, text: language.nativeName, value: language.code }));

    }

    componentDidMount() {

        this.getData();

    }

    getPublishingStatus = (profile: ProfileInterface) => {
        const missingFields: string[] = [];

        // 1. Verificar datos básicos
        if (_.isEmpty(profile.name)) {
            missingFields.push('PROFILE_NAME_MISSING');
        }
        if (_.isEmpty(profile?.pictureUrl)) {
            missingFields.push('PROFILE_PICTURE_MISSING');
        }
        if (_.isEmpty(profile?.about)) {
            missingFields.push('PROFILE_ABOUT_MISSING');
        }

        // 2. Verificar configuraciones de la clase
        if (_.isEmpty(profile?.languages)) {
            missingFields.push('PROFILE_LANGUAGES_MISSING');
        }
        if (_.isEmpty(profile.schedules)) {
            missingFields.push('PROFILE_SCHEDULES_MISSING');
        }
        if (_.isEmpty(profile.modalities)) {
            missingFields.push('PROFILE_MODALITIES_MISSING');
        }

        // 3. Verificar estado de Stripe Connect (asumiendo que viene de la DB)
        if (profile?.stripeVerified !== true) {
            missingFields.push('PROFILE_STRIPE_VERIFICATION_MISSING');
        }

        return {
            isPublishable: missingFields.length === 0,
            missingFields: missingFields,
        };
    }

    getData = async () => {

        const { closeLoader, dispatch, openLoader } = this.props;

        openLoader();

        try {

            const profile = await SecurityModel.getProfile();
            const initialValues = _.merge(
                _.pick(profile, ['about', 'city', 'comercialName', 'country', 'email', 'gender', 'name', 'pictureUrl', 'surname', '_id', 'videoUrl', 'languages', 'publicProfile', 'specialties', 'schedules', 'modalities', 'cancellations']),
                { instagram: profile.ssmm?.instagram, facebook: profile.ssmm?.facebook, web: profile.ssmm?.web, genderSelected: '' + profile?.gender }
            );

            const data = this.getPublishingStatus(profile);

            this.setState({ profile, isPublishableProfile: data.isPublishable, publishMissingFields: data.missingFields });

            dispatch(initialize(
                'profileForm',
                {
                    ...initialValues,
                    publicProfile: String(initialValues.publicProfile)
                }
            ));

            // Politicas de cancelacion globales
            SecurityModel.getProDirectoryConfig().then(data => {

                this.setState({
                    refundPercent: data.refundPercent
                });

            }).catch(error => {

                console.error(error);

            })

        } catch (e) { } finally {

            closeLoader();

        }

    };

    onSubmit = async (values: FormProfileInterface) => {

        const { closeLoader, openLoader, updateProfile } = this.props;

        const { profile, isImageDeleted, isVideoDeleted } = this.state;

        let hasError: boolean = false;

        try {

            openLoader();

            const tempData = _.cloneDeep(values);

            tempData.publicProfile = (tempData.publicProfile as unknown) === 'true';
            tempData.stripeVerified = profile?.stripeVerified;

            const tempPictureUrlFile = _.get(window, 'pictureUrl.tempFile') as any;
            if (tempPictureUrlFile && tempPictureUrlFile.ref) {

                const formData = new FormData();
                formData.append('avatar', tempPictureUrlFile.ref);

                try {

                    const response = await FilesModel.uploadImage(formData);

                    if (_.get(response, 'data.url', false)) {

                        tempData.pictureUrl = response.data.url;

                    }

                } catch (error) {

                    console.error(error);

                }

            } else if (isImageDeleted) {

                tempData.pictureUrl = '';

            }

            const tempVideoUrlFile = _.get(window, 'videoUrl.tempFile') as any;
            if (tempVideoUrlFile && tempVideoUrlFile.ref) {

                const formData = new FormData();
                formData.append('fileData', tempVideoUrlFile.ref);
                formData.append('path', 'presentationVideo');

                try {

                    const response = await FilesModel.uploadFile(formData, 'video');

                    if (_.get(response, 'data.url', false)) {

                        tempData.videoUrl = response.data.url;

                    }

                } catch (error) {

                    console.error(error);

                }

            } else if (isVideoDeleted) {

                tempData.videoUrl = '';

            }

            _.set(tempData, 'ssmm.facebook', tempData.facebook);
            _.set(tempData, 'ssmm.instagram', tempData.instagram);
            _.set(tempData, 'ssmm.web', tempData.web);
            _.set(tempData, 'gender', parseInt(tempData.genderSelected || '0'));

            delete tempData.facebook;
            delete tempData.instagram;
            delete tempData.web;
            delete tempData.genderSelected;

            await updateProfile(tempData);
            this.goBack();

        } catch (error) {

            openToast({ message: I18n.t(`messages.saveError`), type: 'error' });
            hasError = true;

        } finally {

            !hasError && _.set(window, 'pictureUrl', undefined);
            !hasError && _.set(window, 'videoUrl', undefined);
            closeLoader();

        }

    }

    onRemove = (path: string) => {
        this.props.dispatch(autofill('profileForm', path, ''));

        if (path === 'pictureUrl') this.setState({ isImageDeleted: true });
        else if (path === 'videoUrl') this.setState({ isVideoDeleted: true });
    }

    goBack = () => this.props.navigate('/setup/account');

    stripeConnect = async (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const { closeLoader, openLoader } = this.props;

        // 1. Abrimos la nueva pestaña INMEDIATAMENTE (síncronamente).
        // Esto el navegador sí lo permite porque está ligado directo al click.
        const newWindow = window.open('', '_blank');

        // Opcional: Escribir un mensaje o poner un loader en la nueva pestaña
        // para que el usuario sepa que está pasando algo.
        if (newWindow) {
            newWindow.document.write(`<h3>${I18n.t('setup.publishProfileTexts.connectingStripe')}</h3>`);
            newWindow.document.title = I18n.t('messages.loading');
        }

        try {
            openLoader();

            // 2. Hacemos la llamada asíncrona mientras la pestaña espera en blanco
            const onboardingUrl = await PaymentModel.stripeConnectOnboarding();

            if (!onboardingUrl || onboardingUrl?.message) {
                throw new Error("URL de onboarding no recibida.");
            }

            // 3. Si todo salió bien, redirigimos la pestaña que ya abrimos
            if (newWindow) {
                newWindow.location.href = onboardingUrl;
            }

        } catch (error) {
            // 4. Si hay error, CERRAMOS la pestaña que abrimos para no dejarla "huerfana"
            if (newWindow) {
                newWindow.close();
            }

            openToast({ message: I18n.t(`setup.stripeConnect.error`), type: 'error' });

        } finally {
            closeLoader();
        }
    }

    render() {

        const { reduxFormState } = this.props;

        const { profile, countrySelectOptions, genderSelectOptions, languageSelectOptions, specialtiesSelectOptions, isPublishableProfile, publishMissingFields, refundPercent } = this.state;

        const directoryFee = profile?.directoryFee || 10;

        return (
            <Wrapper>
                <PanelNav active='setup' />
                <SetupNav active='account' />
                <Container style={{ paddingTop: '20px' }}>
                    <div className='inner'>
                        <div className='p-flex'>
                            <div className='p-content'>
                                <div className='a-wrapper'>
                                    <div className='a-f-wrapper'>
                                        <div className='setupAccount'>
                                            <Form noValidate onSubmit={this.props.handleSubmit(this.onSubmit)} name='profileForm'>
                                                <Segment>
                                                    <div className='formHeader'>
                                                        <h3>{I18n.t('setup.personalData')}</h3>
                                                        <Popup
                                                            disabled={isPublishableProfile === true}
                                                            trigger={
                                                                <div>
                                                                    <Field
                                                                        component={CustomCheckBox}
                                                                        placeholder={I18n.t('setup.publishProfile')}
                                                                        name='publicProfile'
                                                                        fieldClasses='label-primary'
                                                                        label={I18n.t('setup.publishProfile')}
                                                                        fluid
                                                                        toggle={true}
                                                                        disabled={!this.state.isPublishableProfile}
                                                                    />
                                                                </div>
                                                            }>
                                                            <PopupHeader>
                                                                {I18n.t('setup.publishProfileTexts.validations.title')}
                                                            </PopupHeader>
                                                            <PopupContent>
                                                                <ul style={{ paddingLeft: 20 }}>
                                                                    {publishMissingFields.map((fieldKey) => (
                                                                        <li key={fieldKey}>{I18n.t(`setup.publishProfileTexts.validations.${fieldKey}`)}</li>
                                                                    ))}
                                                                </ul>
                                                            </PopupContent>
                                                        </Popup>
                                                    </div>
                                                    <div className='pictureBox'>
                                                        <label className='primary-font regular'>{I18n.t('setup.picture')}</label>
                                                        <MultimediaManage formFieldName='pictureUrl' type={MultimediaTypes.IMAGE} src={reduxFormState.pictureUrl} onRemove={this.onRemove} />
                                                    </div>
                                                    <div className='videoBox'>
                                                        <label className='primary-font regular'>{I18n.t('setup.video')}</label>
                                                        <MultimediaManage formFieldName='videoUrl' type={MultimediaTypes.VIDEO} src={reduxFormState.videoUrl} onRemove={this.onRemove} />
                                                    </div>
                                                    <Form.Group widths='equal'>
                                                        <Field
                                                            component={CustomInput}
                                                            placeholder={I18n.t('setup.name')}
                                                            name='name'
                                                            fieldClasses='label-primary'
                                                            label={I18n.t('setup.name')}
                                                            fluid
                                                        />
                                                        <Field
                                                            component={CustomInput}
                                                            placeholder={I18n.t('setup.surname')}
                                                            name='surname'
                                                            fieldClasses='label-primary'
                                                            label={I18n.t('setup.surname')}
                                                            fluid
                                                        />
                                                    </Form.Group>
                                                    <Form.Group widths='equal'>
                                                        <Field
                                                            component={CustomSelect}
                                                            placeholder={I18n.t('setup.country')}
                                                            name='country'
                                                            fieldClasses='label-primary'
                                                            label={I18n.t('setup.country')}
                                                            search={true}
                                                            options={countrySelectOptions}
                                                            fluid
                                                        />
                                                        <Field
                                                            component={CustomInput}
                                                            placeholder={I18n.t('setup.city')}
                                                            name='city'
                                                            fieldClasses='label-primary'
                                                            label={I18n.t('setup.city')}
                                                            fluid
                                                        />
                                                    </Form.Group>
                                                    <Field
                                                        component={CustomSelect}
                                                        placeholder={I18n.t('setup.gender')}
                                                        name='genderSelected'
                                                        fieldClasses='label-primary'
                                                        label={I18n.t('setup.gender')}
                                                        options={genderSelectOptions}
                                                    />
                                                    <Field
                                                        component={CustomTextArea}
                                                        placeholder={I18n.t('setup.about')}
                                                        name='about'
                                                        fieldClasses='label-primary'
                                                        label={I18n.t('setup.about')}
                                                    />
                                                    <Field
                                                        component={CustomSelect}
                                                        placeholder={I18n.t('setup.specialties')}
                                                        name='specialties'
                                                        fieldClasses='label-primary'
                                                        label={I18n.t('setup.specialties')}
                                                        search={true}
                                                        multiple={true}
                                                        options={specialtiesSelectOptions}
                                                    />
                                                    <Field
                                                        component={CustomInput}
                                                        placeholder={I18n.t('setup.comercialName')}
                                                        name='comercialName'
                                                        fieldClasses='label-primary'
                                                        label={I18n.t('setup.comercialName')}
                                                    />
                                                    <Field
                                                        component={CustomSelect}
                                                        placeholder={I18n.t('setup.languages')}
                                                        name='languages'
                                                        fieldClasses='label-primary'
                                                        label={I18n.t('setup.languages')}
                                                        search={true}
                                                        multiple={true}
                                                        options={languageSelectOptions}
                                                    />
                                                    <Form.Group widths='equal'>
                                                        <Field
                                                            component={CustomInput}
                                                            placeholder={'Facebook'}
                                                            name='facebook'
                                                            fieldClasses='label-primary'
                                                            label={'Facebook'}
                                                        />
                                                        <Field
                                                            component={CustomInput}
                                                            placeholder={'Instagram'}
                                                            name='instagram'
                                                            fieldClasses='label-primary'
                                                            label={'Instagram'}
                                                        />
                                                    </Form.Group>
                                                    <Field
                                                        component={CustomInput}
                                                        placeholder={I18n.t('setup.webPage')}
                                                        name='web'
                                                        fieldClasses='label-primary'
                                                        label={I18n.t('setup.webPage')}
                                                    />
                                                </Segment>
                                                <Segment>
                                                    <h3>{I18n.t('setup.schedules.title')}</h3>
                                                    <Field
                                                        component={CustomSchedules}
                                                        name='schedules'
                                                        fieldClasses='label-primary'
                                                        fluid
                                                    />
                                                </Segment>
                                                <Segment>
                                                    <h3>{I18n.t('setup.modalities.title')}</h3>
                                                    <Field
                                                        component={CustomPracticeModalities}
                                                        name='modalities'
                                                        fieldClasses='label-primary'
                                                        fluid
                                                        directoryFee={directoryFee}
                                                    />
                                                </Segment>
                                                <Segment>
                                                    <h3>{I18n.t('setup.billingData')}</h3>
                                                    <p>{I18n.t('setup.stripeConnect.description')}</p>
                                                    <OButton $upper fluid $color='#FFF' $stripe onClick={this.stripeConnect}>
                                                        <span>{I18n.t('setup.stripeConnect.text')}</span>
                                                    </OButton>
                                                </Segment>
                                                <Segment>
                                                    <h3>{I18n.t('setup.cancellations.title')}</h3>
                                                    <p>{I18n.t('setup.cancellations.subtitle')}</p>
                                                    <div className='cancellationsField'>
                                                        <Field
                                                            component={CustomInput}
                                                            name='cancellations.cancellationDaysAfterStart'
                                                            fieldClasses='label-primary'
                                                            label={I18n.t('setup.cancellations.daysGrace.label')}
                                                            type="number"
                                                            min="0"
                                                        />
                                                        <p>{I18n.t('setup.cancellations.daysGrace.description')}</p>
                                                        <p>{I18n.t('setup.cancellations.daysGrace.example')}</p>
                                                    </div>
                                                    <div className='cancellationsField'>
                                                        <Field
                                                            component={CustomInput}
                                                            name='cancellations.noticeDaysBeforeEnd'
                                                            fieldClasses='label-primary'
                                                            label={I18n.t('setup.cancellations.minimunDays.label')}
                                                            type="number"
                                                            min="0"
                                                        />
                                                        <p>{I18n.t('setup.cancellations.minimunDays.description')}</p>
                                                        <p dangerouslySetInnerHTML={{ __html: I18n.t('setup.cancellations.minimunDays.example', { refundPercent }) }} />
                                                    </div>
                                                </Segment>
                                                <OButton $upper fluid type='submit' $color='#FFF' $terciary disabled={this.props.invalid}>
                                                    <span>{I18n.t('buttons.actions.save')}</span>
                                                </OButton>
                                            </Form>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <VerifyStripeAccount />
                </Container>
                <BottomNav active='setup' />
            </Wrapper>
        );

    }

}

const validate = (formValues: FormProfileInterface) => {

    const errors = {
        name: undefined,
        surname: undefined,
        city: undefined,
        facebook: undefined,
        instagram: undefined,
        cancellations: {
            cancellationDaysAfterStart: undefined,
            noticeDaysBeforeEnd: undefined
        }
    };

    if (_.isEmpty(formValues.name)) {

        errors.name = I18n.t('validations.required');

    }

    if (_.isEmpty(formValues.surname)) {

        errors.surname = I18n.t('validations.required');

    }

    if (_.isEmpty(formValues.city)) {

        errors.city = I18n.t('validations.required');

    }

    if (!_.isEmpty(formValues.facebook) && !/^(?:(?:http|https):\/\/)?(?:www.|[a-z]{2}-[a-z]{2}.)?facebook.com\/[^ "]+?$/gi.test(formValues.facebook || '')) {

        errors.facebook = I18n.t('validations.facebookUrl');

    }

    if (!_.isEmpty(formValues.instagram) && !/^(?:(?:http|https):\/\/)?(?:www.|[a-z]{2}-[a-z]{2}.)?instagram.com\/[^ "]+?$/gi.test(formValues.instagram || '')) {

        errors.instagram = I18n.t('validations.instagramUrl');

    }

    if (!_.isEmpty(formValues?.cancellations?.cancellationDaysAfterStart) && (formValues.cancellations as any).cancellationDaysAfterStart < 0) {

        errors.cancellations.cancellationDaysAfterStart = I18n.t('validations.minZero')

    }

    if (!_.isEmpty(formValues?.cancellations?.noticeDaysBeforeEnd) && (formValues.cancellations as any).noticeDaysBeforeEnd < 0) {

        errors.cancellations.noticeDaysBeforeEnd = I18n.t('validations.minZero')

    }

    return errors;

};

const form = reduxForm<{}, ProfileProps>({
    form: 'profileForm',
    touchOnBlur: true,
    touchOnChange: false,
    validate
})(ProfileData);

export default connect(state => {

    const reduxFormState = _.cloneDeep(_.get(state, 'form.profileForm.values', {}))

    return { reduxFormState };

}, { closeLoader, openLoader, openToast, updateProfile })(form);